import type { DynamicPricingOffer } from "@/api/offers";
import type { RawMessage } from "@/api/messages";
import { Button } from "@penmate/react-shared/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatUsd } from "@/checkout/requirements";
import { getOfferBadgeText, getOfferEffectiveCredits, getOfferUnitPriceText } from "@/checkout/dynamicPricing";
import { CheckIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { optionCardClass } from "@/components/checkout/optionCard";
import { useAutoAnimate } from "@formkit/auto-animate/react";

const toLowerString = (value: unknown): string => {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
};

const getMessageType = (letter: RawMessage | null): "letter" | "postcard" => {
  const t = (letter as any)?.messageType ?? (letter as any)?.data?.messageType ?? (letter as any)?.order_type;
  return toLowerString(t) === "postcard" ? "postcard" : "letter";
};

const getSelectedPhotosCount = (letter: RawMessage | null): number => {
  const v = (letter as any)?.selectedPhotos ?? (letter as any)?.data?.selectedPhotos ?? (letter as any)?.photos;
  return Array.isArray(v) ? v.length : 0;
};

const getCreditOption = (letter: RawMessage | null): string | null => {
  const v = (letter as any)?.creditOption ?? (letter as any)?.data?.creditOption;
  const s = typeof v === "string" || typeof v === "number" ? String(v) : "";
  return s ? s : null;
};

const getOfferRedemptionId = (letter: RawMessage | null): string | null => {
  const v = (letter as any)?.offerRedemptionId ?? (letter as any)?.offer_redemption_id ?? (letter as any)?.data?.offerRedemptionId;
  const s = typeof v === "string" ? v : "";
  return s ? s : null;
};

const getCreditOptions = (letter: RawMessage | null): Record<string, number> => {
  const meta = (letter as any)?.meta ?? (letter as any)?.data?.meta ?? {};
  const raw = meta?.creditOptions ?? meta?.credit_options;
  if (!raw || typeof raw !== "object") return {};
  const res: Record<string, number> = {};
  for (const key of Object.keys(raw)) {
    const n = Number((raw as any)[key]);
    if (Number.isFinite(n)) res[String(key)] = Math.round(n);
  }
  return res;
};

const computeDefaultCreditOption = (args: {
  bundleSizes: number[];
  creditsDue: number;
  creditsAvailable: number;
  offersByBundle: Record<string, DynamicPricingOffer> | null;
}): string | null => {
  const { bundleSizes, creditsDue, creditsAvailable, offersByBundle } = args;
  const needed = Math.max(creditsDue - creditsAvailable, 0);
  if (needed <= 0) return null;
  const sorted = bundleSizes.filter((n) => n > 0).sort((a, b) => a - b);
  const match =
    sorted.find((n) => {
      if (n <= 0) return false;
      if (n === 1) return n >= needed;
      const offer = offersByBundle ? offersByBundle[String(n)] : null;
      const effective = getOfferEffectiveCredits({ bundleSize: n, offer });
      return creditsAvailable + effective >= creditsDue;
    }) ?? sorted[sorted.length - 1];
  return match != null ? String(match) : null;
};

export function CreditsSheet(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  checkoutLetter: RawMessage | null;
  creditsDue: number;
  creditsAvailable: number;
  offersByBundle: Record<string, DynamicPricingOffer> | null;
  hideSingleCreditOption?: boolean;
  onSave: (args: { creditOption: string; offerRedemptionId: string | null }) => Promise<void>;
}) {
  const { open, onOpenChange, checkoutLetter, creditsDue, creditsAvailable, offersByBundle, onSave, hideSingleCreditOption } =
    props;
  const hideSingleOption = !!hideSingleCreditOption;
  const [optionsAutoAnimateRef] = useAutoAnimate<HTMLDivElement>();

  const messageType = useMemo(() => getMessageType(checkoutLetter), [checkoutLetter]);
  const selectedPhotosCount = useMemo(() => getSelectedPhotosCount(checkoutLetter), [checkoutLetter]);
  const creditOptions = useMemo(() => getCreditOptions(checkoutLetter), [checkoutLetter]);
  const bundleSizes = useMemo(() => {
    const sizes = Object.keys(creditOptions)
      .map((k) => Number.parseInt(k, 10))
      .filter((n) => Number.isFinite(n) && (!hideSingleOption || n !== 1));

    // Returning users (single hidden) keep the full bundle ladder.
    if (hideSingleOption) return sizes;

    // New users get a simplified ladder: the single option plus the smallest
    // bundle whose effective credits (incl. offer bonus) cover this letter's
    // shortfall. Larger bundles appear only when nothing smaller covers.
    const bundles = sizes.filter((n) => n > 1).sort((a, b) => a - b);
    const covering = bundles.find((n) => {
      const offer = offersByBundle ? offersByBundle[String(n)] : null;
      const effective = getOfferEffectiveCredits({ bundleSize: n, offer });
      return creditsAvailable + effective >= creditsDue;
    });
    if (covering == null) return sizes;

    return sizes.filter((n) => n === 1 || n === covering);
  }, [creditOptions, hideSingleOption, offersByBundle, creditsAvailable, creditsDue]);

  const initialSelection = useMemo(() => {
    const persisted = getCreditOption(checkoutLetter);
    if (persisted) {
      const n = Number.parseInt(persisted, 10);
      const size = Number.isFinite(n) ? Math.max(0, n) : 0;
      // A persisted option that is no longer rendered (e.g. a larger bundle
      // hidden by the simplified new-user ladder) falls through to the default.
      if (bundleSizes.includes(size) && !(size === 1 && hideSingleOption)) {
        const singleDisabled = size === 1 && messageType === "letter" && selectedPhotosCount > 1;
        // Legacy semantics: option "1" is a special "single message/postcard" purchase choice,
        // not a literal "1 credit" top-up. Treat it as valid as long as it isn't disabled.
        if (size === 1 && !singleDisabled) return persisted;
        const offer = size > 1 && offersByBundle ? offersByBundle[String(size)] : null;
        const effective = size > 1 ? getOfferEffectiveCredits({ bundleSize: size, offer }) : size;
        const disabled = size > 0 ? creditsAvailable + effective < creditsDue : true;
        if (!disabled) return persisted;
      }
    }
    return computeDefaultCreditOption({
      bundleSizes,
      creditsDue,
      creditsAvailable,
      offersByBundle: offersByBundle || null,
    });
  }, [checkoutLetter, bundleSizes, creditsDue, creditsAvailable, messageType, selectedPhotosCount, hideSingleOption]);

  const [creditOption, setCreditOption] = useState<string | null>(initialSelection);
  const [offerRedemptionId, setOfferRedemptionId] = useState<string | null>(getOfferRedemptionId(checkoutLetter));
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const isOptionDisabled = (size: number): boolean => {
    if (size === 1) {
      if (hideSingleOption) return true;
      if (messageType === "letter" && selectedPhotosCount > 1) return true;
      return false;
    }
    const offer = offersByBundle ? offersByBundle[String(size)] : null;
    const effective = getOfferEffectiveCredits({ bundleSize: size, offer });
    return creditsAvailable + effective < creditsDue;
  };

  const isSelectionValid = (selection: string | null): boolean => {
    if (!selection) return false;
    const size = Number.parseInt(selection, 10);
    if (!Number.isFinite(size) || size <= 0) return false;
    // A selection must be rendered to stay valid: the new-user ladder can
    // shrink mid-open (offers arriving async change which bundle covers),
    // and an affordable-but-hidden selection would otherwise survive.
    if (!bundleSizes.includes(size)) return false;
    return !isOptionDisabled(size);
  };

  const wasOpenRef = useRef(false);
  const lastLetterIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return;
    }
    const letterId = (checkoutLetter as any)?.id ? String((checkoutLetter as any)?.id) : null;
    const opening = !wasOpenRef.current;
    const letterChanged = letterId && letterId !== lastLetterIdRef.current;
    if (opening || letterChanged || !isSelectionValid(creditOption)) {
      setCreditOption(initialSelection);
      const current = getOfferRedemptionId(checkoutLetter);
      const selectedSize = initialSelection ? Number.parseInt(initialSelection, 10) : 0;
      const desired =
        selectedSize > 1 && offersByBundle ? offersByBundle[String(selectedSize)]?.redemption_id ?? null : null;
      setOfferRedemptionId(desired || current);
      setIsSaving(false);
      setSaveError(null);
    }
    wasOpenRef.current = true;
    lastLetterIdRef.current = letterId;
  }, [
    open,
    (checkoutLetter as any)?.id,
    initialSelection,
    offersByBundle,
    creditOption,
    creditsDue,
    creditsAvailable,
    hideSingleOption,
    messageType,
    selectedPhotosCount,
  ]);

  const optionRows = useMemo(() => {
    const sortedSizes = bundleSizes.filter((n) => n > 0).sort((a, b) => a - b);
    return sortedSizes.map((size) => {
      const unitPriceCents = creditOptions[String(size)] ?? 0;
      const baselineSubtotalCents = Math.max(0, Math.round(unitPriceCents * size));
      const offer = offersByBundle ? offersByBundle[String(size)] : null;
      const unitText = getOfferUnitPriceText({
        bundleSize: size,
        offer,
        fallbackText: `$${(Math.max(0, unitPriceCents) / 100).toFixed(2)} ea`,
      });
      const effectiveCredits = getOfferEffectiveCredits({ bundleSize: size, offer });
      const badgeText = getOfferBadgeText(offer);
      return {
        size,
        unitPriceCents,
        baselineSubtotalCents,
        offer,
        badgeText,
        unitText,
        effectiveCredits,
      };
    });
  }, [bundleSizes, creditOptions, offersByBundle]);

  const onSelect = (size: number) => {
    setCreditOption(String(size));
    const offer = offersByBundle ? offersByBundle[String(size)] : null;
    const nextRedemptionId = offer?.redemption_id ?? null;
    setOfferRedemptionId(nextRedemptionId);
  };

  const canSave = !!creditOption && !isOptionDisabled(Number.parseInt(creditOption, 10));

  const save = async () => {
    if (!creditOption) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      await onSave({ creditOption, offerRedemptionId });
      onOpenChange(false);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
      setIsSaving(false);
    }
  };

  return (
    // Disable Radix scroll-lock: it can prevent scrolling in fixed/fullscreen overlays on mobile WebViews.
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogContent className="pm-sheet w-[calc(100vw-1rem)] max-w-md !p-0 overflow-hidden">
        <DialogTitle className="sr-only">Credits</DialogTitle>
        <DialogDescription className="sr-only">Choose a credits bundle.</DialogDescription>

        <div className="flex min-h-0 flex-col">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold">Credits</div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                Choose how many credits to add.
              </div>
            </div>
          </div>

          <div
            className="min-h-0 flex-1 overflow-y-auto px-4 py-4"
            style={{
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
              touchAction: "pan-y",
            }}
          >
            <div className="mb-3 text-xs text-muted-foreground">
              {creditsDue} due / {creditsAvailable} available
            </div>

            <div className="grid grid-cols-1 gap-2" ref={optionsAutoAnimateRef}>
              {optionRows.map((row) => {
                const selected = creditOption === String(row.size);
                const disabled = isOptionDisabled(row.size);
                const showBonus =
                  row.size > 1 && row.offer && (row.offer.bonus_credits ?? 0) && row.effectiveCredits > row.size;

                return (
                  <button
                    key={row.size}
                    type="button"
                    className={optionCardClass({ selected, disabled })}
                    disabled={disabled}
                    onClick={() => onSelect(row.size)}
                    data-testid={`credits-option-${row.size}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        {row.size === 1 ? (
                          <>
                            <div className="text-sm font-medium">
                              Single {messageType === "postcard" ? "Postcard" : "Message"}
                            </div>
                            {messageType !== "postcard" && selectedPhotosCount > 1 ? (
                              <div className="mt-0.5 text-xs text-destructive">
                                You have attached too many photos for this option.
                              </div>
                            ) : (
                              <div className="mt-0.5 text-xs text-muted-foreground">
                                Send 1 message{messageType === "postcard" ? "" : " + 1 photo"}
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <div className="text-sm font-medium">
                              {showBonus ? (
                                <>
                                  {row.size} credits + {row.effectiveCredits - row.size} bonus credits
                                </>
                              ) : (
                                <>Add {row.size} credits</>
                              )}
                            </div>
                            <div className="mt-0.5 text-xs text-muted-foreground">
                              {row.unitText} · {formatUsd(row.baselineSubtotalCents)}
                            </div>
                          </>
                        )}
                        {row.badgeText ? (
                          <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-800">
                            <span aria-hidden="true">🎁</span>
                            <span>{row.badgeText}</span>
                          </div>
                        ) : null}
                      </div>
                      {selected ? (
                        <span className="mt-0.5 text-emerald-600">
                          <CheckIcon className="size-4" aria-hidden="true" />
                        </span>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>

            {saveError ? <div className="mt-3 text-xs text-destructive">{saveError}</div> : null}
          </div>

          <div className="flex items-center justify-end gap-2 border-t px-4 py-3">
            <DialogClose asChild>
              <Button type="button" variant="ghost" disabled={isSaving}>
                Cancel
              </Button>
            </DialogClose>
            <Button type="button" onClick={() => void save()} disabled={isSaving || !canSave} data-testid="credits-save">
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
